/**
 * Model-based searches for CLASSIC-evaluable positions (no gimmicks, no
 * unspent active abilities). Pure JS, browser-compatible.
 *
 *   chooseNormalClassic — 2-ply shallow search (the historical sim AI):
 *     ply 1 = all pawn moves + pruned walls scored by path-length eval,
 *     ply 2 = the best TOP_K moves refined with the opponent's best
 *     immediate reply.
 *
 *   chooseHardClassic — EXACT full-width 2-ply alpha-beta: every legal
 *     wall anchor is considered at the root (normal only looks near the
 *     current shortest paths) and the opponent's best reply is computed
 *     over their full move set with jump-aware pawn moves (normal's reply
 *     approximation ignores jumps, so it can miss jump-wins). The top
 *     root moves are then refined two plies deeper (opponent's best few
 *     replies x my exact 2-ply value = a pruned 4-ply look). Plus an
 *     endgame race rule: once the opponent is out of walls and our path
 *     is not longer, sprint — that is a proven win.
 *
 * Both validate the chosen move with the core before returning.
 */

import { legalMoves, pawnMoves, isLegal } from '../core/index.js';
import {
  DIRS,
  stepBlocked,
  wallConflicts,
  buildModel,
  applyVirtual,
  undoVirtual,
  evalModel,
  modelPawnMoves,
  candidateWalls,
  bfsDistance,
  bfsPath,
} from './model.js';

const WALL_WEIGHT = 0.1; // secondary term: walls-in-hand advantage
const EPS = 1e-9;

/** a === b with a tolerance that also handles +/-Infinity (Inf-Inf is NaN). */
function same(a, b) {
  return a === b || Math.abs(a - b) <= EPS;
}

// ---------------------------------------------------------------------------
// normal: 2-ply shallow search (identical to the historical sim 'ai')
// ---------------------------------------------------------------------------

const NORMAL_TOP_K = 8;

/**
 * Opponent's best immediate reply value (from THEIR perspective) on the
 * virtual model. Approximation for ply 2: pawn replies are plain orthogonal
 * steps (no jump generation — pawns don't affect BFS anyway) and wall
 * replies use the same near-path pruning with a conflict + connectivity
 * check. Returns Infinity if the opponent can win on the spot.
 */
function bestReplyEval(m, opp) {
  let best = -Infinity;
  const pawn = m.pawns[opp];

  // Pawn steps.
  for (const { dc, dr } of DIRS) {
    const c = pawn.col + dc;
    const r = pawn.row + dr;
    if (c < 0 || r < 0 || c >= m.size || r >= m.size) continue;
    if (stepBlocked(m.wallSet, pawn.col, pawn.row, c, r)) continue;
    if (r === m.goalRows[opp]) return Infinity; // opponent wins next move
    const undo = applyVirtual(m, opp, { type: 'move', to: { col: c, row: r } });
    const s = evalModel(m, opp, WALL_WEIGHT);
    undoVirtual(m, undo);
    if (s !== null && s > best) best = s;
  }

  // Wall replies.
  if (m.wallsLeft[opp] > 0) {
    for (const w of candidateWalls(m)) {
      if (wallConflicts(m.wallSet, w.col, w.row, w.orientation)) continue;
      const undo = applyVirtual(m, opp, {
        type: 'wall',
        at: { col: w.col, row: w.row },
        orientation: w.orientation,
      });
      const s = evalModel(m, opp, WALL_WEIGHT); // null if it seals someone in
      undoVirtual(m, undo);
      if (s !== null && s > best) best = s;
    }
  }
  return best;
}

/** Shared ply-1 candidate generation + static scoring on the model. */
function scoredRootCandidates(state, model, me, wallWeight) {
  const candidates = [];
  for (const to of pawnMoves(state, me)) {
    candidates.push({ type: 'move', to: { col: to.col, row: to.row } });
  }
  if (state.players[me].wallsLeft > 0) {
    for (const w of candidateWalls(model)) {
      if (wallConflicts(model.wallSet, w.col, w.row, w.orientation)) continue;
      candidates.push({
        type: 'wall',
        at: { col: w.col, row: w.row },
        orientation: w.orientation,
      });
    }
  }

  const scored = [];
  for (const mv of candidates) {
    if (mv.type === 'move' && mv.to.row === model.goalRows[me]) {
      scored.push({ mv, s1: Infinity });
      continue;
    }
    const undo = applyVirtual(model, me, mv);
    const s = evalModel(model, me, wallWeight);
    undoVirtual(model, undo);
    if (s === null) continue; // wall would seal a player in
    scored.push({ mv, s1: s });
  }
  return scored;
}

/** Final selection shared by both classic searches. */
function pickBest(state, top, rng) {
  let best = top[0];
  for (const item of top) {
    if (
      (!same(item.s2, best.s2) && item.s2 > best.s2) ||
      (same(item.s2, best.s2) && !same(item.s1, best.s1) && item.s1 > best.s1)
    ) {
      best = item;
    }
  }
  const tied = top.filter(
    (item) => same(item.s2, best.s2) && same(item.s1, best.s1)
  );
  const chosen = tied[Math.floor(rng() * tied.length)];

  // Final safety net: the pruned generator produced this without core
  // validation; core applyMove would throw anyway, but check here so a
  // rules divergence surfaces as a clear message.
  if (!isLegal(state, chosen.mv)) {
    throw new Error('AI produced an illegal move: ' + JSON.stringify(chosen.mv));
  }
  return chosen.mv;
}

export function chooseNormalClassic(state, rng) {
  const me = state.current;
  // Pass-only positions (e.g. a hexed opponent whose whole turn is skipped)
  // never reach the classic path via the generic route, but a spent-ability
  // classic-eval position can — return the forced pass instead of generating
  // walls the (hex-unaware) model would wrongly offer.
  const forced = legalMoves(state);
  if (forced.length === 1) return forced[0];
  const model = buildModel(state);

  const scored = scoredRootCandidates(state, model, me, WALL_WEIGHT);

  // Immediate win available: take it.
  const wins = scored.filter((x) => x.s1 === Infinity);
  if (wins.length > 0) {
    return wins[Math.floor(rng() * wins.length)].mv;
  }

  if (scored.length === 0) {
    // Defensive fallback (should not happen): any legal move.
    const all = legalMoves(state);
    return all[Math.floor(rng() * all.length)];
  }

  // --- ply-2 refinement on the top K --------------------------------
  scored.sort((a, b) => b.s1 - a.s1); // stable sort -> deterministic
  const top = scored.slice(0, NORMAL_TOP_K);
  for (const item of top) {
    const undo = applyVirtual(model, me, item.mv);
    const reply = bestReplyEval(model, 1 - me);
    undoVirtual(model, undo);
    // Antisymmetric eval: my value after their best reply = -reply.
    item.s2 = reply === Infinity ? -Infinity : -reply;
  }

  return pickBest(state, top, rng);
}

// ---------------------------------------------------------------------------
// hard: exact full-width 2-ply alpha-beta + endgame race rule
// ---------------------------------------------------------------------------

// Hard values walls-in-hand far more than normal. Self-play showed the
// decisive strategic error at this search depth is overspending walls:
// each wall buys ~+1 opponent detour now, but a wall KEPT is worth more
// later (~2 detour steps once pawns are deep in the wall fight). With
// integer path lengths the weight is effectively quantized at multiples
// of 0.5; >2 means "never trade a held wall for less than a +3 swing".
// Measured vs normal (200 games, both colors): 0.1 -> 53.5%, 0.5 -> 58.5%,
// 1.2 -> 64%, 2.2 -> 65.5%, 2.2 + 4-ply refinement -> 75.5%.
// wallWeight/refineRoot/refineReplies are parameterized (see HARD_PARAMS
// in index.js) so this same search backs both the "tough" and "hard"
// tiers — only how much it hoards walls and how deep it refines differ.
const HARD_DEFAULTS = {
  wallWeight: 2.2,
  refineRoot: 8, // root moves refined 2 plies deeper (0 = off)
  refineReplies: 10, // opponent replies considered per refined move
};
const WIN_VALUE = 1e6; // finite so WIN-vs-WIN comparisons stay sane

/**
 * EXACT best-reply value for `opp` (from THEIR perspective): max over
 * their jump-aware pawn moves and every legal wall anchor. `cutoff`
 * enables alpha-beta-style early exit: once the reply value strictly
 * exceeds `cutoff` the root move under evaluation is already worse than
 * the current best, so the exact value no longer matters.
 * Returns Infinity if the opponent can win on the spot.
 */
function exactBestReply(m, opp, wallWeight, cutoff) {
  let best = -Infinity;

  // Pawn replies with full classic rules (jumps + diagonal dodges) — an
  // approximation here would miss opponent jump-wins near the goal.
  for (const to of modelPawnMoves(m, opp)) {
    if (to.row === m.goalRows[opp]) return Infinity;
    const undo = applyVirtual(m, opp, { type: 'move', to });
    const s = evalModel(m, opp, wallWeight);
    undoVirtual(m, undo);
    if (s !== null && s > best) {
      best = s;
      if (best > cutoff) return best;
    }
  }

  if (m.wallsLeft[opp] > 0) {
    // Near-path walls first (likely strongest -> earliest cutoffs), then
    // every remaining anchor for exactness.
    const tried = new Set();
    const tryWall = (col, row, orientation) => {
      const key = orientation + ':' + col + ',' + row;
      if (tried.has(key)) return false;
      tried.add(key);
      if (wallConflicts(m.wallSet, col, row, orientation)) return false;
      const undo = applyVirtual(m, opp, {
        type: 'wall',
        at: { col, row },
        orientation,
      });
      const s = evalModel(m, opp, wallWeight); // null => seals someone in
      undoVirtual(m, undo);
      if (s !== null && s > best) best = s;
      return best > cutoff;
    };
    for (const w of candidateWalls(m)) {
      if (tryWall(w.col, w.row, w.orientation)) return best;
    }
    for (let col = 0; col <= m.size - 2; col++) {
      for (let row = 0; row <= m.size - 2; row++) {
        if (tryWall(col, row, 'h') || tryWall(col, row, 'v')) return best;
      }
    }
  }
  return best;
}

/**
 * Opponent's best replies as a sorted list (exact scan, no cutoff).
 * Entries carry the reply move and its 1-ply value from THEIR perspective.
 * A reply that wins on the spot is returned alone with s = Infinity.
 */
function exactReplyList(m, opp, wallWeight) {
  const out = [];
  for (const to of modelPawnMoves(m, opp)) {
    if (to.row === m.goalRows[opp]) {
      return [{ mv: { type: 'move', to }, s: Infinity }];
    }
    const mv = { type: 'move', to };
    const undo = applyVirtual(m, opp, mv);
    const s = evalModel(m, opp, wallWeight);
    undoVirtual(m, undo);
    if (s !== null) out.push({ mv, s });
  }
  if (m.wallsLeft[opp] > 0) {
    for (const orientation of ['h', 'v']) {
      for (let col = 0; col <= m.size - 2; col++) {
        for (let row = 0; row <= m.size - 2; row++) {
          if (wallConflicts(m.wallSet, col, row, orientation)) continue;
          const mv = { type: 'wall', at: { col, row }, orientation };
          const undo = applyVirtual(m, opp, mv);
          const s = evalModel(m, opp, wallWeight);
          undoVirtual(m, undo);
          if (s !== null) out.push({ mv, s });
        }
      }
    }
  }
  out.sort((a, b) => b.s - a.s); // stable -> deterministic
  return out;
}

/**
 * Exact 2-ply value for the player to move on the model (no core state
 * needed): max over jump-aware pawn moves + all wall anchors of
 * -exactBestReply. Wins score WIN_VALUE. Used as the leaf evaluation of
 * hard's 4-ply refinement. `enough` is a beta-style bound: the caller
 * takes a min over these values, so once the value reaches `enough` the
 * exact figure cannot change the min and the scan stops early.
 */
function exactTwoPlyValue(m, me, wallWeight, enough = Infinity) {
  const goal = m.goalRows[me];
  const scored = [];
  for (const to of modelPawnMoves(m, me)) {
    if (to.row === goal) return WIN_VALUE;
    const mv = { type: 'move', to };
    const undo = applyVirtual(m, me, mv);
    const s = evalModel(m, me, wallWeight);
    undoVirtual(m, undo);
    if (s !== null) scored.push({ mv, s });
  }
  if (m.wallsLeft[me] > 0) {
    for (const orientation of ['h', 'v']) {
      for (let col = 0; col <= m.size - 2; col++) {
        for (let row = 0; row <= m.size - 2; row++) {
          if (wallConflicts(m.wallSet, col, row, orientation)) continue;
          const mv = { type: 'wall', at: { col, row }, orientation };
          const undo = applyVirtual(m, me, mv);
          const s = evalModel(m, me, wallWeight);
          undoVirtual(m, undo);
          if (s !== null) scored.push({ mv, s });
        }
      }
    }
  }
  if (scored.length === 0) return -WIN_VALUE; // stuck (unreachable classically)
  scored.sort((a, b) => b.s - a.s);
  let best = -Infinity;
  for (const item of scored) {
    const undo = applyVirtual(m, me, item.mv);
    const cutoff = best === -Infinity ? Infinity : -best + EPS;
    const reply = exactBestReply(m, 1 - me, wallWeight, cutoff);
    undoVirtual(m, undo);
    const v = reply === Infinity ? -Infinity : -reply;
    if (v > best) {
      best = v;
      if (best >= enough) return best; // caller's min can no longer change
    }
  }
  return best;
}

/** All root candidates for hard: pawn moves + EVERY non-conflicting anchor. */
function hardRootCandidates(state, model, me, wallWeight) {
  const candidates = [];
  for (const to of pawnMoves(state, me)) {
    candidates.push({ type: 'move', to: { col: to.col, row: to.row } });
  }
  if (state.players[me].wallsLeft > 0) {
    for (const orientation of ['h', 'v']) {
      for (let col = 0; col <= model.size - 2; col++) {
        for (let row = 0; row <= model.size - 2; row++) {
          if (wallConflicts(model.wallSet, col, row, orientation)) continue;
          candidates.push({ type: 'wall', at: { col, row }, orientation });
        }
      }
    }
  }

  const scored = [];
  for (const mv of candidates) {
    if (mv.type === 'move' && mv.to.row === model.goalRows[me]) {
      scored.push({ mv, s1: Infinity });
      continue;
    }
    const undo = applyVirtual(model, me, mv);
    const s = evalModel(model, me, wallWeight);
    undoVirtual(model, undo);
    if (s === null) continue; // wall would seal a player in => illegal
    scored.push({ mv, s1: s });
  }
  return scored;
}

export function chooseHardClassic(state, rng, opts = {}) {
  const { wallWeight, refineRoot, refineReplies } = { ...HARD_DEFAULTS, ...opts };
  const me = state.current;
  // Forced pass (e.g. hexed opponent, no other legal move): serve it directly
  // so the model never generates a move that core isLegal would reject.
  const forced = legalMoves(state);
  if (forced.length === 1) return forced[0];
  const model = buildModel(state);

  // Endgame race rule: if the opponent has no walls left and my path is
  // no longer than theirs, sprinting along the shortest path wins (I move
  // first, and nothing can lengthen my path anymore). Only commit when
  // this turn actually shortens the path (pathological body-block cases
  // fall through to the search instead).
  if (model.wallsLeft[1 - me] === 0) {
    const dMe = bfsDistance(
      model.size,
      model.wallSet,
      model.pawns[me],
      model.goalRows[me]
    );
    const dOpp = bfsDistance(
      model.size,
      model.wallSet,
      model.pawns[1 - me],
      model.goalRows[1 - me]
    );
    if (dMe >= 0 && dOpp >= 0 && dMe <= dOpp) {
      let bestD = Infinity;
      let tiedTo = [];
      for (const to of pawnMoves(state, me)) {
        if (to.row === model.goalRows[me]) {
          bestD = 0;
          tiedTo = [to];
          break;
        }
        const undo = applyVirtual(model, me, { type: 'move', to });
        const d = bfsDistance(
          model.size,
          model.wallSet,
          model.pawns[me],
          model.goalRows[me]
        );
        undoVirtual(model, undo);
        if (d >= 0 && d < bestD) {
          bestD = d;
          tiedTo = [to];
        } else if (d === bestD) {
          tiedTo.push(to);
        }
      }
      if (bestD < dMe) {
        const to = tiedTo[Math.floor(rng() * tiedTo.length)];
        const mv = { type: 'move', to: { col: to.col, row: to.row } };
        if (isLegal(state, mv)) return mv;
      }
    }
  }

  const scored = hardRootCandidates(state, model, me, wallWeight);

  const wins = scored.filter((x) => x.s1 === Infinity);
  if (wins.length > 0) {
    return wins[Math.floor(rng() * wins.length)].mv;
  }

  if (scored.length === 0) {
    const all = legalMoves(state);
    return all[Math.floor(rng() * all.length)];
  }

  // Exact 2-ply: process in static order so the best value rises early
  // and later candidates are cut off after a few replies. The cutoff is
  // strict (best + EPS), so exact values are kept for every candidate
  // that ties the best — the rng tie-break stays fair and deterministic.
  scored.sort((a, b) => b.s1 - a.s1); // stable sort -> deterministic
  let best2 = -Infinity;
  for (const item of scored) {
    const undo = applyVirtual(model, me, item.mv);
    const cutoff = best2 === -Infinity ? Infinity : -best2 + EPS;
    const reply = exactBestReply(model, 1 - me, wallWeight, cutoff);
    undoVirtual(model, undo);
    item.s2 = reply === Infinity ? -Infinity : -reply;
    if (item.s2 > best2) best2 = item.s2;
  }

  // --- 4-ply refinement of the best few root moves -------------------
  // s4 = min over the opponent's top replies of my exact 2-ply value in
  // the resulting position. Pruned root moves (strictly worse than best2)
  // never reach this set, so their partial s2 bounds are harmless.
  const viable = scored.filter((x) => x.s2 !== -Infinity);
  if (viable.length === 0 || refineRoot <= 0) {
    return pickBest(state, scored, rng);
  }
  viable.sort((a, b) => b.s2 - a.s2 || b.s1 - a.s1); // stable -> deterministic
  const refined = viable.slice(0, refineRoot);
  let bestS4 = -Infinity;
  for (const item of refined) {
    const undo = applyVirtual(model, me, item.mv);
    const replies = exactReplyList(model, 1 - me, wallWeight).slice(0, refineReplies);
    let worst = Infinity;
    for (const r of replies) {
      if (r.s === Infinity) {
        worst = -WIN_VALUE; // opponent wins on the spot
        break;
      }
      const undoReply = applyVirtual(model, 1 - me, r.mv);
      const v = exactTwoPlyValue(model, me, wallWeight, worst);
      undoVirtual(model, undoReply);
      if (v < worst) worst = v;
      // Alpha-style cut: already refuted below the best refined value
      // (strictly — ties must keep their exact value for the rng break).
      if (worst < bestS4 - EPS) break;
    }
    undoVirtual(model, undo);
    item.s4 = worst === Infinity ? item.s2 : worst;
    if (item.s4 > bestS4) bestS4 = item.s4;
  }

  let best = refined[0];
  for (const item of refined) {
    if (!same(item.s4, best.s4)) {
      if (item.s4 > best.s4) best = item;
      continue;
    }
    if (!same(item.s2, best.s2)) {
      if (item.s2 > best.s2) best = item;
      continue;
    }
    if (!same(item.s1, best.s1) && item.s1 > best.s1) best = item;
  }
  const tied = refined.filter(
    (i) => same(i.s4, best.s4) && same(i.s2, best.s2) && same(i.s1, best.s1)
  );
  const chosen = tied[Math.floor(rng() * tied.length)];
  if (!isLegal(state, chosen.mv)) {
    throw new Error('AI produced an illegal move: ' + JSON.stringify(chosen.mv));
  }
  return chosen.mv;
}

// ---------------------------------------------------------------------------
// hard (v2): iterative-deepening alpha-beta MINIMAX on the model
// ---------------------------------------------------------------------------
//
// The old chooseHardClassic (still used by tough) is an exact full-width
// 2-ply search plus a pruned 4-ply refinement of the top root moves. Even
// with the refinement its effective horizon is short: it cannot see
// multi-move wall strategies (wall chains that force long detours, box-in
// traps, tempo/parity races) — exactly what strong humans exploit. Measured,
// the old hard beats a plain deeper search at THIS eval because a shallow
// path-difference search naturally avoids overspending walls; a deeper one
// finds wall combos that look good at the horizon but lose once the opponent
// simply walks the maze. The design below fixes that with (a) enough depth to
// SEE the maze walked, and (b) an asymmetric eval that stops the overspend.
//
// chooseDeepClassic: iterative-deepening minimax (2..maxPlies plies of real
// moves, root to leaf) with alpha-beta and a FIXED-root-perspective eval:
//
//   * Root + first `wallPlies` plies: pawn moves + offensive wall anchors
//     (offensiveWalls — walls that lengthen the opponent's shortest path).
//     Deeper plies are PAWN-ONLY: strong play needs depth mainly to watch the
//     race play out (a cheap pawn search), while wall plans are set early.
//     This is what makes a 6-ply search affordable in the browser budget.
//   * Move ordering: leaf children by exact evalFixed, interior pawn children
//     by a cheap advancement key (no BFS) so alpha-beta still cuts hard.
//   * Eval (evalFixed): MINIMAX, always from the root player's view, with the
//     opponent's distance passed through a CONCAVE transform (diminishing
//     returns on walling them) and my own distance LINEAR (full fear of being
//     walled) — an asymmetry a symmetric negamax eval cannot express. Plus a
//     TEMPO term for the first-move advantage in a race.
//   * Wins score +/-(WIN_VALUE + depth) so a sooner win beats a later one.
//   * The endgame sprint rule and immediate-win checks are preserved.
//
// Determinism: fixed iteration order, stable sorts, rng only for final
// tie-breaks among exactly-equal moves. The node budget aborts an in-flight
// DEEPER iteration (whose partial result is then discarded — the last fully
// completed depth is used), and node counts are machine-independent, so the
// choice is reproducible everywhere.
const DEEP_DEFAULTS = {
  // Tuned by benchmark against the OLD hard (chooseHardClassic, exact 2-ply
  // + 4-ply refine). Headline sweep (40 games/point, colours alternating,
  // seed 2026, wall6 = walls at all plies, blocking-orientation only):
  //   maxPlies 4 -> ~40%   (a valley: deep enough to overspend walls,
  //                         too shallow to see the maze walked)
  //   maxPlies 6, walls first 3 plies      -> ~57%
  //   maxPlies 6, walls all plies, +cross  -> 72% but ~285ms/move
  //   maxPlies 6, walls all plies, blocking-only, budget 5-7k -> 65-72%
  // So: DEPTH (to watch the race resolve) + walls at every ply (to find the
  // multi-move plans) + blocking-orientation-only walls (halves branching so
  // the above fits the browser budget) is the winning combination.
  wallWeight: 2.2, // walls-in-hand weight (2.2 beat 1.6/2.6/3.0 head to head)
  maxPlies: 6, // total plies root-to-leaf (>=2)
  tempo: 0.5, // side-to-move first-move bonus in the leaf eval
  // Node-count cap (machine-independent => deterministic). Iterative
  // deepening accumulates nodes across depths; an iteration that trips the
  // cap is discarded, so the LAST FULLY COMPLETED depth is used. This bounds
  // worst-case think time. 4000 is the largest cap that keeps the vitest
  // "think time" test (avg of 5 moves on a midgame position, ~176ms measured)
  // under its 200ms limit; it is also the strength/speed knee — below it the
  // search often falls short of depth 5-6 and the win rate drops sharply,
  // above it think time overruns the budget. See sim/README.md for the curve.
  nodeBudget: 4000,
  fullWalls: false, // interior/root wall set: false=offensive-only, true=candidateWalls
  leafWalls: false, // also try walls at the leaf ply (depth 1)
  crossWalls: false, // false = blocking-orientation walls only (halves branching)
  // Generate wall moves only in the first `wallPlies` plies from the root;
  // deeper plies are pawn-only (cheap race lookahead). wallPlies >= maxPlies
  // means walls at every ply.
  wallPlies: 6,
  // Concave distance transform: distances up to `distCap` count fully, the
  // excess is scaled by `distTail`. distTail = 1 is the plain linear
  // dOpp - dMe eval; concave (< 1) was measured HARMFUL here (it hides the
  // danger of your own pawn being walled far away), so linear is the default.
  distCap: 6,
  distTail: 1.0,
};

/** Concave distance value: full up to cap, then scaled by tail (< 1). */
function distValue(d, cap, tail) {
  return d <= cap ? d : cap + (d - cap) * tail;
}

/** True if every step of `path` is still walkable in `wallSet`. */
function pathStillOpen(wallSet, path) {
  for (let i = 0; i + 1 < path.length; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (stepBlocked(wallSet, a.col, a.row, b.col, b.row)) return false;
  }
  return true;
}

/**
 * Leaf evaluation ALWAYS from the fixed root player's (ctx.rootMe)
 * perspective — this is a MINIMAX eval, not negamax, on purpose. The
 * asymmetry is the whole point:
 *
 *   value = distValue(dOpp) - dMe + ww * (myWalls - oppWalls)
 *
 * The opponent's distance is passed through the CONCAVE distValue (walling
 * them from 6 to 20 is only marginally better than 6 to 10 — diminishing
 * returns), while my OWN distance stays LINEAR (I fully fear being walled
 * in). A symmetric/negamax eval cannot express this: making it antisymmetric
 * forces the same concavity on my own distance, which blinds me to my peril
 * (measured: 0-16% vs old). The asymmetry stops the deep search from
 * overspending walls to build pointless mega-mazes while keeping its full
 * instinct to race and to defend. Returns null if either player is sealed
 * off (an illegal wall one ply above).
 */
function evalFixed(m, ctx) {
  const me = ctx.rootMe;
  const opp = 1 - me;
  const dMe = bfsDistance(m.size, m.wallSet, m.pawns[me], m.goalRows[me]);
  if (dMe < 0) return null;
  const dOpp = bfsDistance(m.size, m.wallSet, m.pawns[opp], m.goalRows[opp]);
  if (dOpp < 0) return null;
  return (
    distValue(dOpp, ctx.cap, ctx.tail) -
    dMe +
    ctx.ww * (m.wallsLeft[me] - m.wallsLeft[opp])
  );
}

/**
 * Lean interior wall set: only anchors whose wall could lengthen the
 * OPPONENT's current shortest path (offensive walls — the ones actually
 * worth searching when it is `side`'s turn). Both the blocking orientation
 * and its cross-orientation at each path step are offered, since corner
 * walls (cross-orientation) often force the longest detours. One BFS-path
 * instead of candidateWalls' two, and no self-path walls (almost never the
 * best move), which is what keeps the depth-4 tree affordable.
 */
function offensiveWalls(m, side, out, cross = true) {
  out.length = 0;
  const opp = 1 - side;
  const path = bfsPath(m.size, m.wallSet, m.pawns[opp], m.goalRows[opp]);
  if (!path) return out;
  const size = m.size;
  const seen = _wallSeen;
  seen.clear();
  const add = (col, row, orientation) => {
    if (col < 0 || row < 0 || col > size - 2 || row > size - 2) return;
    const key = orientation === 'h' ? col * 8 + row : 64 + col * 8 + row;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ col, row, orientation });
  };
  for (let i = 0; i + 1 < path.length; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (a.col === b.col) {
      // vertical step -> blocked by h walls; cross = the v walls at the anchor
      const r = Math.min(a.row, b.row);
      add(a.col - 1, r, 'h');
      add(a.col, r, 'h');
      if (cross) {
        add(a.col - 1, r, 'v');
        add(a.col, r, 'v');
      }
    } else {
      const c = Math.min(a.col, b.col);
      add(c, a.row - 1, 'v');
      add(c, a.row, 'v');
      if (cross) {
        add(c, a.row - 1, 'h');
        add(c, a.row, 'h');
      }
    }
  }
  return out;
}
const _wallSeen = new Set();

/**
 * Alpha-beta MINIMAX value of the position with `side` to move, `depth`
 * plies remaining, from the FIXED root player's perspective (ctx.rootMe).
 * MAX nodes are ctx.rootMe's turn, MIN nodes the opponent's. Immediate pawn
 * wins terminate: rootMe reaching goal is +(WIN+depth), the opponent
 * reaching goal is -(WIN+depth) (sooner = larger magnitude). At the leaf
 * (depth <= 1) the best child's static evalFixed is returned, adjusted by
 * the tempo the OTHER side gains by moving next.
 */
function deepSearch(m, side, depth, alpha, beta, ctx) {
  if (ctx.aborted) return 0; // fast unwind: this iteration will be discarded
  ctx.nodes += 1;
  if (ctx.nodes >= ctx.budget) {
    ctx.aborted = true;
    return 0;
  }
  const isMax = side === ctx.rootMe;
  const goal = m.goalRows[side];
  const isLeaf = depth <= 1;

  // Generate children with an ordering score from the fixed root perspective
  // (higher = better for rootMe). At the LEAF the score must be exact
  // (evalFixed = 2 BFS) because it is the returned value. At INTERIOR nodes
  // only the RECURSION value matters, so pawn moves are ordered by a cheap
  // advancement key (no BFS) — this removes almost all BFS from the deep
  // pawn-only plies, which dominate the node count. Walls always get exact
  // evalFixed (it doubles as the seal-legality check).
  const fromRow = m.pawns[side].row;
  const goalDist0 = fromRow > goal ? fromRow - goal : goal - fromRow;
  const children = [];
  for (const to of modelPawnMoves(m, side)) {
    // `side` reaches its goal: a win for whoever just moved.
    if (to.row === goal) return isMax ? WIN_VALUE + depth : -(WIN_VALUE + depth);
    const mv = { type: 'move', to };
    let s;
    if (isLeaf) {
      const undo = applyVirtual(m, side, mv);
      s = evalFixed(m, ctx);
      undoVirtual(m, undo);
      if (s === null) continue; // pawn moves never seal, but stay safe
    } else {
      // Advancement toward `side`'s goal, signed to rootMe's perspective.
      const gd = to.row > goal ? to.row - goal : goal - to.row;
      const adv = goalDist0 - gd; // +1 advance, -1 retreat, 0 sideways
      s = isMax ? adv : -adv;
    }
    children.push({ mv, s });
  }
  // Walls only in the opening plies (depth >= wallMinDepth) and, unless
  // leafWalls, not at the very leaf (depth >= 2).
  const wallPly = depth >= ctx.wallMinDepth && (ctx.leafWalls || depth >= 2);
  if (wallPly && m.wallsLeft[side] > 0) {
    const walls = ctx.fullWalls ? candidateWalls(m) : offensiveWalls(m, side, [], ctx.crossWalls);
    // Fast wall eval: an offensive wall targets the OTHER player (1 - side),
    // so only that player's distance needs a fresh BFS. The mover's OWN
    // shortest path is unchanged unless the wall blocks it — a cheap
    // ~path-length stepBlocked check that avoids the second BFS. Exact: a
    // wall can only lengthen paths, so an untouched shortest path keeps its
    // length. (fullWalls candidates can target either side, so fall back to
    // the plain two-BFS evalFixed there.)
    const me = ctx.rootMe;
    const sidePath = ctx.fullWalls
      ? null
      : bfsPath(m.size, m.wallSet, m.pawns[side], m.goalRows[side]);
    const sideDist0 = sidePath ? sidePath.length - 1 : -1;
    for (const w of walls) {
      if (wallConflicts(m.wallSet, w.col, w.row, w.orientation)) continue;
      const mv = {
        type: 'wall',
        at: { col: w.col, row: w.row },
        orientation: w.orientation,
      };
      const undo = applyVirtual(m, side, mv);
      let s;
      if (sidePath) {
        const target = 1 - side;
        const dTarget = bfsDistance(
          m.size,
          m.wallSet,
          m.pawns[target],
          m.goalRows[target]
        );
        if (dTarget < 0) {
          s = null; // seals the target in
        } else {
          const dSide = pathStillOpen(m.wallSet, sidePath)
            ? sideDist0
            : bfsDistance(m.size, m.wallSet, m.pawns[side], m.goalRows[side]);
          if (dSide < 0) {
            s = null; // seals the mover in
          } else {
            const dMe = side === me ? dSide : dTarget;
            const dOpp = side === me ? dTarget : dSide;
            s =
              distValue(dOpp, ctx.cap, ctx.tail) -
              dMe +
              ctx.ww * (m.wallsLeft[me] - m.wallsLeft[1 - me]);
          }
        }
      } else {
        s = evalFixed(m, ctx); // null => seals someone in
      }
      undoVirtual(m, undo);
      if (s !== null) children.push({ mv, s });
    }
  }
  // No legal move (unreachable classically): treat as a loss for the mover.
  if (children.length === 0) return isMax ? -WIN_VALUE : WIN_VALUE;

  // Order children best-first for the side to move (MAX high, MIN low).
  children.sort((a, b) => (isMax ? b.s - a.s : a.s - b.s)); // stable
  if (depth <= 1) {
    // Leaf: after `side` moves it is the OTHER side's turn, so that side
    // holds the tempo. From rootMe's view that is +tempo when the opponent
    // just moved (rootMe is next), -tempo when rootMe just moved.
    return children[0].s + (isMax ? -ctx.tempo : ctx.tempo);
  }

  const opp = 1 - side;
  if (isMax) {
    let best = -Infinity;
    for (const c of children) {
      const undo = applyVirtual(m, side, c.mv);
      const v = deepSearch(m, opp, depth - 1, alpha, beta, ctx);
      undoVirtual(m, undo);
      if (v > best) best = v;
      if (best > alpha) alpha = best;
      if (alpha >= beta) break; // beta cutoff
    }
    return best;
  }
  let best = Infinity;
  for (const c of children) {
    const undo = applyVirtual(m, side, c.mv);
    const v = deepSearch(m, opp, depth - 1, alpha, beta, ctx);
    undoVirtual(m, undo);
    if (v < best) best = v;
    if (best < beta) beta = best;
    if (alpha >= beta) break; // alpha cutoff
  }
  return best;
}

/**
 * Root candidates for the deep search: pawn moves + offensive wall anchors
 * (blocking/cornering the opponent's shortest path), each with its 1-ply
 * static score for ordering + seal filtering. Immediate pawn wins carry
 * s1 = Infinity. Uses the same lean wall set as the interior so the whole
 * depth-4 tree stays affordable; walls on the mover's own path are never a
 * best root move and are intentionally excluded.
 */
function deepRootCandidates(state, model, me, ctx) {
  const candidates = [];
  for (const to of pawnMoves(state, me)) {
    candidates.push({ type: 'move', to: { col: to.col, row: to.row } });
  }
  if (state.players[me].wallsLeft > 0) {
    const walls = ctx.fullWalls ? candidateWalls(model) : offensiveWalls(model, me, [], ctx.crossWalls);
    for (const w of walls) {
      if (wallConflicts(model.wallSet, w.col, w.row, w.orientation)) continue;
      candidates.push({
        type: 'wall',
        at: { col: w.col, row: w.row },
        orientation: w.orientation,
      });
    }
  }
  const scored = [];
  for (const mv of candidates) {
    if (mv.type === 'move' && mv.to.row === model.goalRows[me]) {
      scored.push({ mv, s1: Infinity });
      continue;
    }
    const undo = applyVirtual(model, me, mv);
    const s = evalFixed(model, ctx); // ctx.rootMe === me at the root
    undoVirtual(model, undo);
    if (s === null) continue;
    scored.push({ mv, s1: s });
  }
  return scored;
}

/**
 * Endgame sprint: if the opponent has no walls left and my path is no
 * longer than theirs, sprinting the shortest path wins outright (I move
 * first; nothing can lengthen my path). Returns the sprint move, or null
 * if the rule does not apply. Mirrors chooseHardClassic's inline rule so
 * that function stays byte-for-byte intact for the tough tier.
 */
function trySprint(state, model, me, rng) {
  if (model.wallsLeft[1 - me] !== 0) return null;
  const dMe = bfsDistance(model.size, model.wallSet, model.pawns[me], model.goalRows[me]);
  const dOpp = bfsDistance(
    model.size,
    model.wallSet,
    model.pawns[1 - me],
    model.goalRows[1 - me]
  );
  if (!(dMe >= 0 && dOpp >= 0 && dMe <= dOpp)) return null;
  let bestD = Infinity;
  let tiedTo = [];
  for (const to of pawnMoves(state, me)) {
    if (to.row === model.goalRows[me]) {
      bestD = 0;
      tiedTo = [to];
      break;
    }
    const undo = applyVirtual(model, me, { type: 'move', to });
    const d = bfsDistance(model.size, model.wallSet, model.pawns[me], model.goalRows[me]);
    undoVirtual(model, undo);
    if (d >= 0 && d < bestD) {
      bestD = d;
      tiedTo = [to];
    } else if (d === bestD) {
      tiedTo.push(to);
    }
  }
  if (bestD >= dMe) return null; // no progress this turn: fall through to search
  const to = tiedTo[Math.floor(rng() * tiedTo.length)];
  const mv = { type: 'move', to: { col: to.col, row: to.row } };
  return isLegal(state, mv) ? mv : null;
}

export function chooseDeepClassic(state, rng, opts = {}) {
  const cfg = { ...DEEP_DEFAULTS, ...opts };
  const ww = cfg.wallWeight;
  const me = state.current;
  const opp = 1 - me;
  // Forced pass (e.g. hexed opponent, no other legal move): serve it directly
  // so the model never generates a move that core isLegal would reject.
  const forced = legalMoves(state);
  if (forced.length === 1) return forced[0];
  const model = buildModel(state);

  // Endgame sprint (proven win) short-circuit.
  const sprint = trySprint(state, model, me, rng);
  if (sprint) return sprint;

  const ctx = {
    rootMe: me, // fixed perspective for the minimax eval
    ww,
    tempo: cfg.tempo,
    cap: cfg.distCap,
    tail: cfg.distTail,
    nodes: 0,
    budget: cfg.nodeBudget,
    aborted: false,
    fullWalls: !!cfg.fullWalls,
    leafWalls: !!cfg.leafWalls,
    crossWalls: cfg.crossWalls !== false,
    // Deepest ply (in `depth` units) at which walls are still generated.
    wallMinDepth: Math.max(2, cfg.maxPlies - cfg.wallPlies + 1),
  };

  // Root candidates: pawn moves + near-path wall candidates, each with its
  // 1-ply static score (initial ordering + seal filtering). Full-width walls
  // at the root are correct but far too slow to then search 3 plies deeper;
  // offensiveWalls covers the strategically relevant anchors. Immediate wins
  // are taken directly.
  const scored = deepRootCandidates(state, model, me, ctx);
  const wins = scored.filter((x) => x.s1 === Infinity);
  if (wins.length > 0) return wins[Math.floor(rng() * wins.length)].mv;
  if (scored.length === 0) {
    const all = legalMoves(state);
    return all[Math.floor(rng() * all.length)];
  }

  scored.sort((a, b) => b.s1 - a.s1); // stable -> deterministic first ordering
  // `order` holds the best COMPLETED iteration's ranking (+ exact vals). An
  // iteration that trips the node budget is discarded wholesale so the
  // ranking never mixes depths — deterministic because node counts are
  // machine-independent.
  let order = scored;
  for (const it of order) it.val = it.s1; // seed vals for the depth-1 fallback
  for (let plies = 2; plies <= cfg.maxPlies; plies++) {
    const searchDepth = plies - 1; // plies below the root move
    const iter = order.map((it) => ({ ...it }));
    let best = -Infinity;
    for (const item of iter) {
      const undo = applyVirtual(model, me, item.mv);
      // After my root move it is the opponent's turn: a MIN node returning
      // my (rootMe) value directly. Aspiration lower bound (alpha = best -
      // EPS, strict) so ties keep exact values for a fair rng tie-break;
      // strictly worse moves may fail low.
      const alpha = best === -Infinity ? -Infinity : best - EPS;
      const v = deepSearch(model, opp, searchDepth, alpha, Infinity, ctx);
      undoVirtual(model, undo);
      if (ctx.aborted) break;
      item.val = v;
      if (v > best) best = v;
    }
    if (ctx.aborted) break; // budget hit mid-iteration: keep previous depth
    // Completed: adopt this iteration's ranking (stable, best-first).
    iter.sort((a, b) => b.val - a.val || b.s1 - a.s1);
    order = iter;
  }

  let best = order[0];
  for (const item of order) {
    if (!same(item.val, best.val)) {
      if (item.val > best.val) best = item;
      continue;
    }
    if (!same(item.s1, best.s1) && item.s1 > best.s1) best = item;
  }
  const tied = order.filter((i) => same(i.val, best.val) && same(i.s1, best.s1));
  const chosen = tied[Math.floor(rng() * tied.length)];
  if (!isLegal(state, chosen.mv)) {
    throw new Error('AI produced an illegal move: ' + JSON.stringify(chosen.mv));
  }
  return chosen.mv;
}
